/**
 * artif-frame-switcher.js
 *
 * Manages four independent state dimensions on generative product pages:
 *
 *   1. ORIENTATION — portrait (3:4) or landscape (4:3)
 *      Listens for changes on the BuddyPix orientation radio inputs.
 *
 *   2. FRAME COLOUR — No Frame, White, Black, Natural, Dark
 *      Listens for Shopify variant radio changes on the Frame option.
 *
 *   3. MATERIAL — Matte, Canvas (or any value from the Material option)
 *      Listens for Shopify variant radio changes on the Material option.
 *      Determines which set of frame PNGs to display (matte vs canvas).
 *
 *   4. ARTWORK — the generated image URL
 *      Selection handled by BuddyPix's updateProductImage() targeting
 *      #artif-artwork-display. This file handles deselection (restoring
 *      placeholder) and variant-change artwork persistence.
 */

(function () {
  'use strict';

  // ── STATE ────────────────────────────────────────────────────────────────

  var state = {
    orientation: 'portrait',
    frameColour: 'no frame',
    material: 'matte',         // 'matte' or 'canvas'
  };

  // ── FRAME INSET VALUES ───────────────────────────────────────────────────
  //
  // All frame PNGs share the same 1:1 (4096×4096) canvas and transparent
  // window. Insets are per-orientation only. If individual frames need
  // different insets in the future, add a nested lookup by colour.
  //
  // How to measure:
  //   1. Open frame PNG in Photoshop (4096×4096 canvas)
  //   2. Select the transparent window area
  //   3. Read pixel coordinates: top-left (x1,y1), bottom-right (x2,y2)
  //   4. top    = y1 / 4096 * 100
  //   5. bottom = (4096 - y2) / 4096 * 100
  //   6. lr     = x1 / 4096 * 100  (assumes symmetric left/right)
  //
  // Current measurements:
  //   Portrait:  window (764, 321) → (3332, 3776)
  //   Landscape: window (321, 764) → (3776, 3332)

  var FRAME_INSETS = {
    portrait:  { top: '7.84%', bottom: '7.81%', lr: '18.65%' },
    landscape: { top: '18.65%', bottom: '18.65%', lr: '7.84%' },
  };

  // ── HELPERS ──────────────────────────────────────────────────────────────

  function getWrapper()    { return document.querySelector('.artif-frame-hero-wrapper'); }
  function getArtworkImg() { return document.getElementById('artif-artwork-display'); }
  function getFrameImgs()  { return document.querySelectorAll('.artif-frame-img'); }

  /** Map a Material option value to our internal key */
  function getMaterialKey(value) {
    return /canvas/i.test(value) ? 'canvas' : 'matte';
  }

  /** Auto-detect orientation from artwork image dimensions */
  function detectAndApplyOrientation(imageUrl) {
    if (!imageUrl) return;
    var img = new Image();
    img.onload = function () {
      var newOrientation = img.naturalWidth > img.naturalHeight ? 'landscape' : 'portrait';
      if (newOrientation !== state.orientation) {
        state.orientation = newOrientation;
        applyState();
        console.log('[ART-IF] Orientation auto-detected:', newOrientation,
          '(' + img.naturalWidth + '×' + img.naturalHeight + ')');
      }
    };
    img.src = imageUrl;
  }

  /** Watch #artif-artwork-display for src changes and auto-detect orientation */
  function observeArtworkOrientation() {
    var artworkImg = getArtworkImg();
    if (!artworkImg) return;

    // Check if artwork is already set (e.g. restored from sessionStorage on page load)
    var currentSrc = artworkImg.src;
    var defaultSrc = artworkImg.getAttribute('data-default-src');
    if (currentSrc && currentSrc !== defaultSrc) {
      detectAndApplyOrientation(currentSrc);
    }

    // Watch for future src changes
    new MutationObserver(function () {
      var src = artworkImg.src;
      var def = artworkImg.getAttribute('data-default-src');
      if (src && src !== def) {
        detectAndApplyOrientation(src);
      }
    }).observe(artworkImg, { attributes: true, attributeFilter: ['src'] });
  }

  // ── CORE: APPLY CURRENT STATE ────────────────────────────────────────────

  function applyState() {
    var wrapper = getWrapper();
    if (!wrapper) return;

    var orientation = state.orientation;
    var frameColour = state.frameColour;
    var material    = state.material;

    // 1. Update wrapper orientation
    wrapper.setAttribute('data-orientation', orientation);

    // 2. Show only the frame image matching colour + orientation + material
    getFrameImgs().forEach(function (img) {
      var colourMatch      = img.getAttribute('frame-color') === frameColour;
      var orientationMatch = img.getAttribute('data-orientation') === orientation;
      var imgMaterial      = img.getAttribute('data-material');
      var materialMatch    = imgMaterial === material || imgMaterial === 'all';
      var isActive         = colourMatch && orientationMatch && materialMatch;
      img.style.display = isActive ? 'block' : 'none';
      img.classList.toggle('artif-frame-img--active', isActive);
    });

    // 3. Apply inset CSS vars
    var inset = FRAME_INSETS[orientation] || FRAME_INSETS.portrait;
    wrapper.style.setProperty('--artif-top', inset.top);
    wrapper.style.setProperty('--artif-bottom', inset.bottom);
    wrapper.style.setProperty('--artif-lr', inset.lr);

    console.log('[ART-IF] State applied:', JSON.stringify(state), inset);
  }

  // ── EVENT LISTENERS ──────────────────────────────────────────────────────

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (input.type !== 'radio') return;

    // Orientation radio (BuddyPix generator UI)
    if (input.value === 'portrait' || input.value === 'landscape') {
      state.orientation = input.value;
      applyState();
      return;
    }

    // Frame variant radio (Shopify variant picker)
    if (input.name && /frame/i.test(input.name)) {
      state.frameColour = input.value.toLowerCase();
      applyState();
      return;
    }

    // Material variant radio (Shopify variant picker)
    if (input.name && /material/i.test(input.name)) {
      state.material = getMaterialKey(input.value);
      applyState();
    }
  });

  // ── ARTWORK DESELECT ─────────────────────────────────────────────────────

  window.addEventListener('buddypix:imageSelected', function (e) {
    var detail = e.detail || {};

    if (!detail.imageId && !detail.imageUrl) {
      // BuddyPix clearArtwork() already restored the original image
      // on #artif-artwork-display — do not overwrite it.
      console.log('[ART-IF] Artwork deselected (BuddyPix handles restoration)');
      return;
    }

    // Artwork selected — auto-detect orientation from image dimensions
    if (detail.imageUrl) {
      detectAndApplyOrientation(detail.imageUrl);
    }
  });

  // ── VARIANT CHANGE: FULL STATE PATCHING ───────────────────────────────────
  //
  // When a variant changes, Horizon replaces the entire <media-gallery> with
  // new server-rendered HTML (media-gallery.js:48 — this.replaceWith()).
  // The new HTML renders with DEFAULT state. We patch ALL current state into
  // the new HTML in the capture phase, BEFORE replaceWith() runs.

  function patchVariantHtml(event) {
    var wrapper = getWrapper();
    if (!wrapper) return;

    var html = event.detail && event.detail.data && event.detail.data.html;
    if (!html) return;

    var newWrapper = html.querySelector('.artif-frame-hero-wrapper');
    if (!newWrapper) return;

    // 1. Patch artwork URL (if artwork is active)
    var artworkImg = getArtworkImg();
    if (artworkImg) {
      var currentSrc = artworkImg.src;
      var defaultSrc = artworkImg.getAttribute('data-default-src');
      if (currentSrc && currentSrc !== defaultSrc) {
        var newArtworkImg = html.querySelector('#artif-artwork-display');
        if (newArtworkImg) {
          newArtworkImg.setAttribute('src', currentSrc);
          newArtworkImg.setAttribute('srcset', '');
          newArtworkImg.removeAttribute('sizes');
        }
      }
    }

    // 2. Patch orientation
    newWrapper.setAttribute('data-orientation', state.orientation);

    // 3. Patch inset CSS vars
    var inset = FRAME_INSETS[state.orientation] || FRAME_INSETS.portrait;
    newWrapper.style.setProperty('--artif-top', inset.top);
    newWrapper.style.setProperty('--artif-bottom', inset.bottom);
    newWrapper.style.setProperty('--artif-lr', inset.lr);

    // 4. Patch active frame overlay visibility (colour + orientation + material)
    var newFrameImgs = newWrapper.querySelectorAll('.artif-frame-img');
    newFrameImgs.forEach(function (img) {
      var colourMatch      = img.getAttribute('frame-color') === state.frameColour;
      var orientationMatch = img.getAttribute('data-orientation') === state.orientation;
      var imgMaterial      = img.getAttribute('data-material');
      var materialMatch    = imgMaterial === state.material || imgMaterial === 'all';
      if (colourMatch && orientationMatch && materialMatch) {
        img.style.display = 'block';
        img.classList.add('artif-frame-img--active');
      } else {
        img.style.display = 'none';
      }
    });
  }

  // ── INIT ─────────────────────────────────────────────────────────────────

  function init() {
    if (!getWrapper()) return;

    // Read current orientation radio state
    var orientationInput = document.querySelector(
      'input[value="portrait"]:checked, input[value="landscape"]:checked'
    );
    if (orientationInput) state.orientation = orientationInput.value;

    // Read current variant radio states (frame + material)
    var checkedRadios = document.querySelectorAll('input[type="radio"]:checked');
    checkedRadios.forEach(function (input) {
      if (input.name && /frame/i.test(input.name)) {
        state.frameColour = input.value.toLowerCase();
      }
      if (input.name && /material/i.test(input.name)) {
        state.material = getMaterialKey(input.value);
      }
    });

    applyState();

    // Auto-detect orientation from artwork (if already set on page load)
    observeArtworkOrientation();

    // Attach capture-phase variant:update listener on the section
    var section = document.querySelector('media-gallery');
    var sectionEl = section && section.closest('.shopify-section');
    if (sectionEl) {
      sectionEl.addEventListener('variant:update', patchVariantHtml, true);

      // Re-init after DOM replacement (media-gallery gets replaced)
      new MutationObserver(function () {
        if (getWrapper()) {
          setTimeout(function () {
            // Re-read variant radio states (radios are NOT replaced, only gallery is)
            var checkedRadios = document.querySelectorAll('input[type="radio"]:checked');
            checkedRadios.forEach(function (input) {
              if (input.name && /frame/i.test(input.name)) {
                state.frameColour = input.value.toLowerCase();
              }
              if (input.name && /material/i.test(input.name)) {
                state.material = getMaterialKey(input.value);
              }
            });
            applyState();
            observeArtworkOrientation();
          }, 50);
        }
      }).observe(sectionEl, { childList: true, subtree: true });
    }

    console.log('[ART-IF] Frame switcher ready. Initial state:', JSON.stringify(state));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
